/**
 * The subset of a vehicle's identifying fields used to correlate a write with the vehicle it
 * was computed for (AVS-4400). All three are optional — a caller may know only one.
 */
export interface IVehicleIdentity {
  prString?: string;
  vinOrMarketingCode?: string;
  okapiCode?: string;
}

export const IDENTITY_FIELDS = ['prString', 'vinOrMarketingCode', 'okapiCode'] as const;

/**
 * True only when at least one identifier is a non-empty present value on both sides and equal.
 * Two absent identifiers are never a match — `undefined === undefined` is not identity, the same
 * hazard `describesCurrentCar` in `state/session/carImages.ts` guards against.
 *
 * These are OR semantics, which is what *finding* a vehicle wants: a configuration change keeps
 * the VIN/okapiCode while the prString moves, and both describe the same car in the registry.
 * Guarding a write wants the opposite — see {@link identityMatchesAll}.
 */
export function identityMatches(a: IVehicleIdentity, b: IVehicleIdentity): boolean {
  return !!(
    (a.prString && a.prString === b.prString) ||
    (a.vinOrMarketingCode && a.vinOrMarketingCode === b.vinOrMarketingCode) ||
    (a.okapiCode && a.okapiCode === b.okapiCode)
  );
}

/**
 * True when EVERY identifier the caller actually supplied is present and equal on both sides, and
 * at least one was supplied. This is the correlation rule for a write guard (AVS-4400).
 *
 * The OR rule above is unsafe here for the exact reason `healDuplicateFields` refuses to heal
 * across two differing prStrings: a configuration switch keeps the VIN and okapiCode, so
 * `{ prString: newConfig, vinOrMarketingCode: sameVin }` matches the OLD configuration on the VIN
 * alone — and the guard would then wave through regulatory data computed for a different config.
 * A supplied identifier the target does not carry at all is a mismatch, not a free pass.
 */
export function identityMatchesAll(expected: IVehicleIdentity, actual: IVehicleIdentity): boolean {
  const supplied = IDENTITY_FIELDS.filter((field) => !!expected[field]);
  return supplied.length > 0 && supplied.every((field) => expected[field] === actual[field]);
}
